import React, { useEffect, useRef, useState } from 'react';

const FaceDetectionOval = ({ 
  showInstructions = true, 
  size = 'default',
  showCornerGuides = true,
  showCenterCrosshair = true,
  orientation = 'vertical',
  videoRef,
  recording = false,
  recordingTime = 0
}) => {
  const [faceDetected, setFaceDetected] = useState(false);
  const [facePosition, setFacePosition] = useState(null);
  const [isInFrame, setIsInFrame] = useState(false);
  const [isPreview, setIsPreview] = useState(true);
  const [faceDetectionDisabled, setFaceDetectionDisabled] = useState(false); // Circuit breaker state
  const [errorCount, setErrorCount] = useState(0); // Track consecutive errors
  const canvasRef = useRef(null);
  const detectionInterval = useRef(null);
  const errorThreshold = 2; // Stop after only 2 consecutive errors (more aggressive)

  // Size configurations for different orientations
  const sizeConfigs = {
    // Vertical orientation (height > width) - for portrait/face recording
    vertical: {
      small: { width: '120px', height: '150px', innerWidth: '110px', innerHeight: '135px' },
      default: { width: '180px', height: '220px', innerWidth: '170px', innerHeight: '205px' },
      large: { width: '240px', height: '280px', innerWidth: '230px', innerHeight: '265px' }
    },
    // Horizontal orientation (width > height) - for landscape recording
    horizontal: {
      small: { width: '150px', height: '120px', innerWidth: '140px', innerHeight: '110px' },
      default: { width: '220px', height: '180px', innerWidth: '210px', innerHeight: '170px' },
      large: { width: '280px', height: '240px', innerWidth: '270px', innerHeight: '230px' }
    }
  };

  const orientationConfig = sizeConfigs[orientation] || sizeConfigs.vertical;
  const config = orientationConfig[size] || orientationConfig.default;

  // Listen for global face detection disable events
  useEffect(() => {
    const handleGlobalDisable = (event) => {
      console.warn('🚫 Global face detection disable triggered:', event.detail);
      setFaceDetectionDisabled(true);
      setErrorCount(errorThreshold); // Set to threshold to prevent restart
      
      // Clear any existing interval
      if (detectionInterval.current) {
        clearInterval(detectionInterval.current);
        detectionInterval.current = null;
      }
    };

    window.addEventListener('disableFaceDetection', handleGlobalDisable);
    
    // TIMEOUT-BASED CIRCUIT BREAKER: Disable face detection after 30 seconds to prevent infinite loops
    const timeoutCircuitBreaker = setTimeout(() => {
      console.warn('🚫 TIMEOUT CIRCUIT BREAKER: Face detection disabled after 30 seconds to prevent infinite loops');
      setFaceDetectionDisabled(true);
      setErrorCount(errorThreshold);
      
      if (detectionInterval.current) {
        clearInterval(detectionInterval.current);
        detectionInterval.current = null;
      }
    }, 30000); // 30 seconds
    
    return () => {
      window.removeEventListener('disableFaceDetection', handleGlobalDisable);
      clearTimeout(timeoutCircuitBreaker);
    };
  }, []);

  // Update preview state based on recording status
  useEffect(() => {
    setIsPreview(!recording);
  }, [recording]);

  // Enhanced face detection with oval boundary checking
  const detectFace = () => {
    // Additional safety check: only run when recording is actually active
    if (!recording) {
      return;
    }
    
    if (!videoRef?.current || !canvasRef?.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    // Check if video is ready and has valid dimensions
    if (!video.videoWidth || !video.videoHeight || video.videoWidth === 0 || video.videoHeight === 0) {
      return; // Skip detection if video isn't ready
    }

    try {
      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get image data for analysis
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Calculate oval boundary for face detection
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const ovalWidth = (parseInt(config.width) / 100) * canvas.width * 0.8; // Increased scale for larger oval
      const ovalHeight = (parseInt(config.height) / 100) * canvas.height * 0.8;

      // Real-time enhanced skin tone detection with oval boundary checking
      let skinPixels = 0;
      let totalPixels = 0;
      let skinPixelsInOval = 0;
      let totalPixelsInOval = 0;

      // Optimize detection by sampling every 2nd pixel for better performance
      for (let y = 0; y < canvas.height; y += 2) {
        for (let x = 0; x < canvas.width; x += 2) {
          const index = (y * canvas.width + x) * 4;
          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];

          // Enhanced skin tone detection with better thresholds
          const isSkinTone = r > 90 && g > 35 && b > 15 && 
            Math.max(r, g, b) - Math.min(r, g, b) > 12 &&
            Math.abs(r - g) > 12 && r > g && r > b &&
            r < 250 && g < 250 && b < 250; // Exclude very bright pixels

          if (isSkinTone) {
            skinPixels++;
          }
          totalPixels++;

          // Check if pixel is within oval boundary
          const normalizedX = (x - centerX) / (ovalWidth / 2);
          const normalizedY = (y - centerY) / (ovalHeight / 2);
          const isInOval = (normalizedX * normalizedX + normalizedY * normalizedY) <= 1;

          if (isInOval) {
            if (isSkinTone) {
              skinPixelsInOval++;
            }
            totalPixelsInOval++;
          }
        }
      }

      // Calculate skin pixel percentages
      const overallSkinPercentage = (skinPixels / totalPixels) * 100;
      const ovalSkinPercentage = totalPixelsInOval > 0 ? (skinPixelsInOval / totalPixelsInOval) * 100 : 0;
      
      // Enhanced detection logic with adjusted thresholds for larger oval
      const hasFaceOverall = overallSkinPercentage > 0.2; // Lower threshold for overall detection
      const hasFaceInOval = ovalSkinPercentage > 15; // Adjusted threshold for larger oval area
      
      // Determine face detection status
      const faceDetected = hasFaceOverall && hasFaceInOval;
      const inFrame = hasFaceInOval; // Face is properly positioned in oval
      
      setFaceDetected(faceDetected);
      setIsInFrame(inFrame);
      
      // Log detection results (less frequent for performance)
      if (Math.random() < 0.1) { // Only log 10% of the time
        if (faceDetected && inFrame) {
          console.log('✅ Face detected and in frame:', {
            overall: overallSkinPercentage.toFixed(2) + '%',
            oval: ovalSkinPercentage.toFixed(2) + '%'
          });
        } else if (faceDetected && !inFrame) {
          console.log('⚠️ Face detected but out of frame:', {
            overall: overallSkinPercentage.toFixed(2) + '%',
            oval: ovalSkinPercentage.toFixed(2) + '%'
          });
        } else {
          console.log('❌ No face detected:', {
            overall: overallSkinPercentage.toFixed(2) + '%',
            oval: ovalSkinPercentage.toFixed(2) + '%'
          });
        }
      }
    } catch (error) {
      console.warn('Face detection error:', error);
      
      // CIRCUIT BREAKER: Track errors and disable face detection if too many occur
      setErrorCount(prev => {
        const newCount = prev + 1;
        console.warn(`🚫 Face detection error ${newCount}/${errorThreshold}:`, error.message || error);
        
        if (newCount >= errorThreshold) {
          console.warn(`🚫 CIRCUIT BREAKER: ${errorThreshold} consecutive errors reached. Disabling face detection.`);
          setFaceDetectionDisabled(true);
          
          // Stop the detection interval immediately
          if (detectionInterval.current) {
            clearInterval(detectionInterval.current);
            detectionInterval.current = null;
          }
          
          // Force disable the component
          setFaceDetectionDisabled(true);
        }
        return newCount;
      });
      
      // Don't update faceDetected state on error
    }
  };

  // Start face detection
  useEffect(() => {
    // CIRCUIT BREAKER: Don't start face detection if it's been disabled due to errors
    if (faceDetectionDisabled) {
      console.warn('🚫 Face detection disabled due to circuit breaker. Not starting detection.');
      return;
    }

    if (videoRef?.current && recording) { // Only run when recording
      // Wait for video to be ready before starting detection
      const handleVideoReady = () => {
        if (detectionInterval.current) {
          clearInterval(detectionInterval.current);
        }
        // Start detection interval only when recording
        detectionInterval.current = setInterval(detectFace, 100);
      };

      // Check if video is already ready
      if (videoRef.current.readyState >= 2) {
        handleVideoReady();
      } else {
        // Wait for video to be ready
        videoRef.current.addEventListener('loadeddata', handleVideoReady);
      }
      
      return () => {
        if (detectionInterval.current) {
          clearInterval(detectionInterval.current);
        }
        if (videoRef.current) {
          videoRef.current.removeEventListener('loadeddata', handleVideoReady);
        }
      };
    } else {
      // Clear interval when not recording
      if (detectionInterval.current) {
        clearInterval(detectionInterval.current);
        detectionInterval.current = null;
      }
    }
  }, [videoRef, config, recording, faceDetectionDisabled]); // Add faceDetectionDisabled to dependencies

  // Reset circuit breaker when recording starts fresh
  useEffect(() => {
    if (recording && faceDetectionDisabled) {
      console.log('🔄 Recording started, resetting circuit breaker');
      setFaceDetectionDisabled(false);
      setErrorCount(0);
    }
  }, [recording, faceDetectionDisabled]);

  // Additional safety: immediately clear interval when recording stops
  useEffect(() => {
    if (!recording && detectionInterval.current) {
      console.log('🛑 Recording stopped, clearing face detection interval');
      clearInterval(detectionInterval.current);
      detectionInterval.current = null;
    }
  }, [recording]);

  // Complete disable if circuit breaker is triggered
  if (faceDetectionDisabled) {
    console.warn('🚫 FaceDetectionOval completely disabled due to circuit breaker');
    return null; // Render nothing
  }

  // Determine oval color based on state
  const getOvalColor = () => {
    if (isPreview) return 'border-white'; // White for preview/idle
    if (faceDetected && isInFrame) return 'border-green-500'; // Green when face detected and in frame
    if (faceDetected && !isInFrame) return 'border-yellow-500'; // Yellow when face detected but out of frame
    return 'border-red-500'; // Red when no face detected
  };

  const getOvalOpacity = () => {
    if (isPreview) return 'border-opacity-60';
    if (faceDetected && isInFrame) return 'border-opacity-90';
    if (faceDetected && !isInFrame) return 'border-opacity-80';
    return 'border-opacity-80';
  };

  const getInnerOvalColor = () => {
    if (isPreview) return 'border-white';
    if (faceDetected && isInFrame) return 'border-green-400';
    if (faceDetected && !isInFrame) return 'border-yellow-400';
    return 'border-red-400';
  };

  const getInnerOvalOpacity = () => {
    if (isPreview) return 'border-opacity-40';
    if (faceDetected && isInFrame) return 'border-opacity-70';
    if (faceDetected && !isInFrame) return 'border-opacity-60';
    return 'border-opacity-60';
  };

  const getGlowColor = () => {
    if (isPreview) return '0 0 20px rgba(255, 255, 255, 0.3)';
    if (faceDetected && isInFrame) return '0 0 25px rgba(34, 197, 94, 0.6)'; // Green glow
    if (faceDetected && !isInFrame) return '0 0 25px rgba(234, 179, 8, 0.6)'; // Yellow glow
    return '0 0 25px rgba(239, 68, 68, 0.6)'; // Red glow
  };

  const getCrosshairColor = () => {
    if (isPreview) return 'bg-white';
    if (faceDetected && isInFrame) return 'bg-green-400';
    if (faceDetected && !isInFrame) return 'bg-yellow-400';
    return 'bg-red-400';
  };

  const getInstructionsText = () => {
    if (isPreview) return "Position your face within the oval guide for optimal recording";
    if (faceDetected && isInFrame) return "Perfect! Face detected and in frame. Recording in progress...";
    if (faceDetected && !isInFrame) return "Face detected! Please center your face within the oval";
    return "Please position your face within the oval guide";
  };

  const getInstructionsBg = () => {
    if (isPreview) return 'bg-black bg-opacity-70 text-white';
    if (faceDetected && isInFrame) return 'bg-green-600 bg-opacity-90 text-white';
    if (faceDetected && !isInFrame) return 'bg-yellow-600 bg-opacity-90 text-black';
    return 'bg-red-600 bg-opacity-90 text-white';
  };

  const formatTime = (seconds) => {
    return `${seconds}s`;
  };

  return (
    <div className="absolute inset-0 flex pointer-events-none">
      {/* Hidden canvas for face detection */}
      <canvas 
        ref={canvasRef} 
        className="hidden"
        style={{ position: 'absolute', top: '-9999px' }}

      />
      
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Mobile-specific positioning adjustment - accounts for tall mobile viewport */}
        <div className="transform -translate-y-8 md:translate-y-0">
        {/* Inner oval guide for precise positioning - Responsive sizing */}
        <div 
          className={`border-2 ${getInnerOvalColor()} ${getInnerOvalOpacity()} rounded-full transition-all duration-300 w-[230px] h-[265px] md:w-[180px] md:h-[220px]`}
          style={{
            boxShadow: getGlowColor()
          }}
        ></div>
        
        {/* Corner guides for better positioning */}
        {showCornerGuides && (
          <>
            <div className={`absolute -top-2 -left-2 w-4 h-4 border-l-2 border-t-2 ${getInnerOvalColor()} ${getInnerOvalOpacity()} transition-all duration-300`}></div>
            <div className={`absolute -top-2 -right-2 w-4 h-4 border-r-2 border-t-2 ${getInnerOvalColor()} ${getInnerOvalOpacity()} transition-all duration-300`}></div>
            <div className={`absolute -bottom-2 -left-2 w-4 h-4 border-l-2 border-b-2 ${getInnerOvalColor()} ${getInnerOvalOpacity()} transition-all duration-300`}></div>
            <div className={`absolute -bottom-2 -right-2 w-4 h-4 border-r-2 border-b-2 ${getInnerOvalColor()} ${getInnerOvalOpacity()} transition-all duration-300`}></div>
          </>
        )}
        </div>
      </div>
      
      {/* Instructions overlay */}
      {showInstructions && (
        <div className="absolute -top-16 md:-top-20 left-4 right-4">
          <div className={`px-3 py-2 rounded-lg text-center ${getInstructionsBg()} transition-all duration-300`}>
            <p className="text-xs font-medium">
              {getInstructionsText()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaceDetectionOval; 